import React, { useState, useEffect, useMemo } from "react";
import { Typography, Box, Button, FormControl, Select, MenuItem } from '@mui/material';
import { useUserAndGameContext } from "../../../contexts/UserAndGameContext";
import { useSuperPickemContext } from "../context/SuperPickemContext";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@emotion/react";
import { useMediaQuery } from "@mui/material";
import './SuperPickemLeaderboard.scss';
import SuperPickemOverallLeaderboard from "./SuperPickemOverallLeaderboard";
import InfoIcon from '@mui/icons-material/Info';
import TiebreakerModal from '../../GameModals/TiebreakerInfoModal/TiebreakerModal';
import useNavigateToTop from "../../../hooks/general/useNavigateToTop";
import { DateTime } from "luxon";
import FilterSwitch from "../../GamePageComponents/FilterSwitch/FilterSwitch";
import { max } from "lodash";


const SuperPickemLeaderboardNew = () => {

    const {
        masterPoolData,
        gameData,
        league,
        hasJoinDeadlinePassed,
        leaderboardData,
        myUserId,
        gameAddress,
        masterPoolId,
        gameId,
        myLeaderboardInfo,
        allUsersEntries,
        usersIndexesInThisPool,
        isGameOver,
    } = useUserAndGameContext();

    const {
        teams,
        hasFinalStarted,
        hasBracketDeadlinePassed,
        hasFirstGameStarted,
    } = useSuperPickemContext();


    const hostInfo = masterPoolData?.gameParameters?.hostInfo;
    const hostIds = hostInfo?.hostIds;
    const hostBackgroundColor = hostInfo?.hostBackgroundColor;

    const tiebreakerInfo = masterPoolData?.tiebreakerInfo;

    const maxPossiblePoints = masterPoolData?.gameParameters?.maxPossiblePoints;

    const pickingTotals = masterPoolData?.gameParameters?.pickingTotals;

    const rounds = masterPoolData?.gameParameters?.rounds;
    const lastRound = rounds ? rounds[rounds.length - 1] : null;

    function findChampId(picks) {
        if (!picks || !lastRound) return null;
        
        const champPick = picks?.find(pick => pick.round === lastRound);
        return champPick?.teamId;
    }

    function findChampResult(picks) {
        if (!picks || !lastRound) return null;
        
        const champPick = picks?.find(pick => pick.round === lastRound);
        return champPick?.result;
    }

    const location = useLocation();
    const navigate = useNavigate();
    const navigateToTop = useNavigateToTop();

    const [tiebreakInfoOpen, setTiebreakInfoOpen] = useState(false);
    const [hostFilter, setHostFilter] = useState(false);
    
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    

     // Initialize selectedTab based on URL query parameter
     const [selectedTab, setSelectedTab] = useState(() => {
        const searchParams = new URLSearchParams(location.search);
        const tabParam = searchParams.get('tab');
        
        // Check if the tab parameter is valid (overall, bracket, or picks)
        const validTabs = ['overall', 'bracket', 'picks'];
        return validTabs.includes(tabParam) ? tabParam : 'overall';
    });

    const handleTabChange = (tab) => {
        // Update local state
        setSelectedTab(tab);

        // Update URL parameters
        const searchParams = new URLSearchParams(location.search);
        
        // If tab is overall, remove the tab parameter
        if (tab === 'overall') {
            searchParams.delete('tab');
        } else {
            searchParams.set('tab', tab);
        }

        // Update URL without triggering a page reload
        navigate(`${location.pathname}?${searchParams.toString()}`, { replace: true });
    };

    const uriEncoded = (link) => {
        return encodeURI(`${encodeURIComponent(link)}`);
    }

    const handleRulesClick = () => {
        navigateToTop(`/${uriEncoded(gameAddress)}?mId=${uriEncoded(masterPoolId)}&gId=${uriEncoded(gameId)}&page=rules`);
    }
    

    //  // Filter data based on hostFilter
    const filteredData = useMemo(() => {
        if (!leaderboardData) return [];
        
        if (hostFilter && hostInfo?.hostIds) {
            // Include only users that are hosts or the current user
            return leaderboardData.filter(user => 
                hostInfo.hostIds.includes(user.userId) || user.userId === myUserId
            );
        }
        
        return leaderboardData;
    }, [leaderboardData, hostFilter, hostInfo, myUserId]);

    const handleUsernameClick = (username) => {
        // Set the username in the context        
        // Navigate to the bracket page with the username as a URL parameter
        navigate(`/super-pickem?mId=${encodeURIComponent(masterPoolId)}&gId=${encodeURIComponent(gameId)}&page=bracket&user=${encodeURIComponent(username)}`);
    };

     // Get entries for this pool
     const entriesInThisPool = usersIndexesInThisPool
     ?.filter(index => index >= 0 && index < allUsersEntries?.length)
     ?.map(index => allUsersEntries[index]);
    //console.log("ENTRIES IN THIS POOL LEADERBOARD", entriesInThisPool);

    const findUserEntry = (entryName) => {
        return entriesInThisPool?.find(entry => entry.name === entryName)?.pool?.bracketPicks || null;
    };



    //console.log("Overall Rank Placeholder:", overallRankPlaceholder, "Bracket Rank Placeholder:", bracketRankPlaceholder, "Pickem Rank Placeholder:", pickemRankPlaceholder);
    return (
        <>
         <Box className="super-pickem-leaderboard-button-container">

            <Button
                className={`super-pickem-leaderboard-button ${selectedTab === 'overall' ? 'active' : ''}`}
                onClick={() => handleTabChange('overall')}
            >
                Super Pick 'Em
            </Button>

            <Button
                className={`super-pickem-leaderboard-button ${selectedTab === 'bracket' ? 'active' : ''}`}
                onClick={() => handleTabChange('bracket')}
            >
                Bracket
            </Button>

            <Button
                className={`super-pickem-leaderboard-button ${selectedTab === 'picks' ? 'active' : ''}`}
                onClick={() => handleTabChange('picks')}
            >
                Pick 'Em
            </Button>

        </Box>

        <Box
            sx={{
                display: 'flex',
                flexDirection: 'row',
                margin: '0 auto',
                justifyContent: 'center',
                alignItems: 'center',
                marginTop: '0px',
                marginBottom: '12px',
                cursor: 'pointer',
            }}
            onClick={() => setTiebreakInfoOpen(true)}
        >
            <Typography
                sx={{
                    fontSize: isMobile ? '12px' : '18px',
                    fontWeight: '500',
                    textDecoration: 'underline',
                    color: '#00AAD1',
                    marginRight: '5px',
                    marginTop: '10px',
                }}
            >
                Tiebreaker Info
            </Typography>
            <InfoIcon
                sx={{
                    color: '#00AAD1',
                    fontSize: isMobile ? '16px' : '20px',
                }}
            />
        </Box>

        {hostInfo && (
                <FilterSwitch
                    hostName={hostInfo?.hostName}
                    hostFilter={hostFilter}
                    setHostFilter={setHostFilter}
                />
        )}

        <SuperPickemOverallLeaderboard
            myLeaderboardInfo={myLeaderboardInfo}
            hasBracketDeadlinePassed={hasBracketDeadlinePassed}
            teams={teams}
            leaderboard={filteredData}
            maxPossiblePoints={maxPossiblePoints}
            myUserId={myUserId}
            handleUsernameClick={handleUsernameClick}
            hostFilter={hostFilter}
            hasFinalStarted={hasFinalStarted}
            isGameOver={isGameOver}
            entriesInThisPool={entriesInThisPool}
            findUserEntry={findUserEntry}
            findChampId={findChampId}
            findChampResult={findChampResult}
            league={league}
            hostIds={hostIds}
            hostBackgroundColor={hostBackgroundColor}
            selectedTab={selectedTab}
            pickingTotals={pickingTotals}
        />
        
        {tiebreakInfoOpen && (
            <TiebreakerModal
                tiebreakInfoOpen={tiebreakInfoOpen}
                setTiebreakInfoOpen={setTiebreakInfoOpen}
                tiebreakerInfo={tiebreakerInfo}
                handleRulesClick={handleRulesClick}
            />
        )}

</>

    )
}

export default SuperPickemLeaderboardNew;